import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { displayWind, windUnit } from '@/lib/units';
import { matchGear, hasKites, gradeDayFit } from '@/lib/gearMatch';
import {
  Wind, Waves, CheckCircle2, AlertTriangle, XCircle, Target,
} from 'lucide-react';

const STATUS_COLOR = {
  ideal: '#2EE59D',
  usable_small: '#FFB020', usable_big: '#FFB020',
  unsuitable_small: '#FF6B35', unsuitable_big: '#FF6B35',
};
const VERDICT = {
  good: { color: '#2EE59D', Icon: CheckCircle2 },
  warn: { color: '#FFB020', Icon: AlertTriangle },
  none: { color: '#FF6B35', Icon: XCircle },
};

// 5-level fit scale for the "coming days" tiles (dark green → dark red).
const FIT_LEVELS = {
  5: { color: '#16A34A', key: 'perfect' },   // dark green
  4: { color: '#4ADE80', key: 'good' },       // green
  3: { color: '#FFB020', key: 'marginal' },   // orange
  2: { color: '#FB7185', key: 'poor' },       // light red
  1: { color: '#DC2626', key: 'nogo' },       // dark red
};
const LEGEND_ORDER = [5, 4, 3, 2, 1];

const KiteChip = ({ k, t }) => (
  <div
    className="flex items-center justify-between gap-2 rounded-lg bg-[#0A0A0A] border px-2.5 py-1.5"
    style={{ borderColor: `${STATUS_COLOR[k.status]}40` }}
    data-testid={`gearmatch-kite-${k.size}`}
  >
    <span className="font-data text-sm text-white/90">
      {k.size} m²{k.brand ? <span className="text-muted-foreground"> · {k.brand}</span> : ''}
    </span>
    <span className="label-eyebrow text-[10px]" style={{ color: STATUS_COLOR[k.status] }}>
      {t(`gearMatch.status.${k.status}`)}
    </span>
  </div>
);

export const MyGearMatch = ({ spot, forecast, nowKn, units, user }) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2);

  const now = useMemo(() => {
    if (!user) return null;
    return matchGear(user.equipment, {
      windKnots: nowKn,
      weightKg: user.weight_kg,
      skill: user.skill_level,
      spotType: spot.type,
    });
  }, [user, nowKn, spot.type]);

  const days = useMemo(() => {
    if (!user) return [];
    return (forecast?.daily || []).slice(0, 5).map((d) => {
      const grade = gradeDayFit(user.equipment, {
        windKnots: d.wind_max,
        weightKg: user.weight_kg,
        skill: user.skill_level,
        spotType: spot.type,
      });
      return { date: d.date, windKn: d.wind_max, grade };
    });
  }, [user, forecast, spot.type]);

  // Render nothing when there is no rider or no kites to match (by design).
  if (!user || !hasKites(user.equipment) || !now) return null;

  const { rec, kites, boards, verdict } = now;
  const v = VERDICT[verdict.level] || VERDICT.none;
  const VIcon = v.Icon;

  const verdictText = !rec.rideable
    ? t('gearMatch.lightWind')
    : t(`gearMatch.verdict.${verdict.level === 'empty' ? 'none' : verdict.level}`, {
        size: verdict.best?.size,
        min: rec.min,
        max: rec.max,
      });

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="kite-card p-5"
      data-testid="my-gear-match"
    >
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-semibold flex items-center gap-2">
          <Target className="w-4 h-4 text-[#00B4D8]" /> {t('gearMatch.title')}
        </h3>
        <span className="label-eyebrow text-muted-foreground hidden sm:inline">
          {t('gearMatch.recommended', { min: rec.min, max: rec.max })}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{t('gearMatch.subtitle')}</p>

      {/* Verdict banner */}
      <div
        className="flex items-start gap-2.5 rounded-xl p-3.5 mb-4"
        style={{ background: `${v.color}14`, border: `1px solid ${v.color}33` }}
        data-testid="gearmatch-verdict"
      >
        <VIcon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: v.color }} />
        <p className="text-sm text-white/90 leading-snug">{verdictText}</p>
      </div>

      {/* Your kites */}
      <div className="label-eyebrow text-[#48CAE4] mb-2 flex items-center gap-1.5">
        <Wind className="w-3.5 h-3.5" /> {t('gearMatch.yourKites')}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="gearmatch-kites">
        {kites.map((k, i) => <KiteChip key={`${k.size}-${i}`} k={k} t={t} />)}
      </div>

      {/* Your board(s) */}
      {boards.length > 0 && (
        <>
          <div className="label-eyebrow text-[#48CAE4] mt-4 mb-2 flex items-center gap-1.5">
            <Waves className="w-3.5 h-3.5" /> {t('gearMatch.yourBoard')}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="gearmatch-boards">
            {boards.map((b, i) => (
              <span
                key={i}
                className="text-xs px-2.5 py-1.5 rounded-lg bg-[#0A0A0A] border flex items-center gap-1.5"
                style={{ borderColor: b.status === 'ideal' ? '#2EE59D40' : '#FFB02040' }}
              >
                <span className="text-white/85">{t(`gearMatch.disciplines.${b.discipline}`)}</span>
                <span style={{ color: b.status === 'ideal' ? '#2EE59D' : '#FFB020' }} className="label-eyebrow text-[10px]">
                  {t(`gearMatch.boardStatus.${b.status}`)}
                </span>
              </span>
            ))}
          </div>
        </>
      )}

      {/* Forecast windows: does the rider's own gear fit each upcoming day? */}
      {days.length > 0 && (
        <>
          <div className="label-eyebrow text-muted-foreground mt-5 mb-2">{t('gearMatch.forecastTitle')}</div>
          <div className="grid grid-cols-5 gap-2" data-testid="gearmatch-forecast">
            {days.map((d) => {
              const lvl = d.grade?.level || 1;
              const meta = FIT_LEVELS[lvl] || FIT_LEVELS[1];
              const color = meta.color;
              const best = d.grade?.best;
              return (
                <div
                  key={d.date}
                  className="rounded-xl p-2 text-center transition-colors"
                  style={{ background: `${color}1A`, border: `1px solid ${color}59` }}
                  title={t(`gearMatch.fit.${meta.key}`)}
                  data-testid={`gearmatch-day-${d.date}`}
                  data-fit-level={lvl}
                >
                  <div className="text-[10px] text-muted-foreground capitalize">
                    {new Date(d.date).toLocaleDateString(locale, { weekday: 'short' })}
                  </div>
                  <div className="font-data text-sm font-semibold text-white/90 mt-0.5">
                    {displayWind(d.windKn, units)}<span className="text-[9px] text-muted-foreground">{windUnit(units)}</span>
                  </div>
                  <div className="font-data text-xs font-semibold mt-1" style={{ color }}>
                    {best ? `${best.size}m` : '—'}
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                    <span className="text-[9px] font-medium leading-none" style={{ color }}>
                      {t(`gearMatch.fit.${meta.key}`)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Colour legend — dark green (perfect) → dark red (no-go) */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3" data-testid="gearmatch-legend">
            {LEGEND_ORDER.map((l) => (
              <span key={l} className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: FIT_LEVELS[l].color }} />
                <span className="text-[10px] text-muted-foreground">{t(`gearMatch.fit.${FIT_LEVELS[l].key}`)}</span>
              </span>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
};
